import React, { useEffect, useState } from "react";
import {
    Page,
    Text,
    View,
    Document,
    StyleSheet,
    Image
} from "@react-pdf/renderer";
import { IP } from "../../internalServer"

const blue = "#1565c0";
const lightBlue = "#e3f2fd";

const styles = StyleSheet.create({
    page: {
        padding: 20,
        fontSize: 9,
        fontFamily: "Helvetica",
        backgroundColor: "#fff",
    },

    // ── HEADER ──────────────────────────────────────────────
    headerBox: {
        border: "1pt solid #ccc",
        borderRadius: 4,
        padding: "10 14",
        marginBottom: 6,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
    },
    headerCenter: {
        flex: 1,
        alignItems: "center",
    },
    schoolName: {
        fontSize: 20,
        color: blue,
        fontFamily: "Helvetica-Bold",
        textAlign: "center",
    },
    reportTitle: {
        fontSize: 13,
        color: blue,
        fontFamily: "Helvetica-Bold",
        textAlign: "center",
        marginTop: 3,
    },
    sessionText: {
        fontSize: 11,
        textAlign: "center",
        marginTop: 4,
    },
    logoBox: {
        width: 50,
        height: 50,
        border: "1pt solid #aaa",
        borderRadius: 24,
        alignItems: "center",
        justifyContent: "center",
    },
    logoText: {
        fontSize: 6,
        color: blue,
        fontFamily: "Helvetica-Bold",
        textAlign: "center",
    },

    // ── STUDENT INFO ─────────────────────────────────────────
    infoBox: {
        border: "1pt solid #ccc",
        borderRadius: 4,
        padding: 12,
        marginBottom: 6,
        flexDirection: "row",
    },
    infoLeft: { flex: 1 },
    infoRight: { flex: 1 },
    infoPhotoBox: {
        width: 70,
        height: 90,
        border: "1pt solid #ccc",
        borderRadius: 4,
        alignItems: "center",
        justifyContent: "center",
        marginLeft: 8,
    },
    infoLine: {
        flexDirection: "row",
        marginBottom: 5,   // increase spacing
        paddingVertical: 2, // add vertical padding
    },
    infoLabel: {
        width: 100,
        color: "#333",
        fontFamily: "Helvetica-Bold",
        fontSize: 9,
    },
    infoColon: {
        width: 8,
        fontSize: 9,
    },
    infoValue: {
        fontFamily: "Helvetica-Bold",
        color: blue,
        fontSize: 9,
        flex: 1,
    },

    // ── SECTION HEADING ──────────────────────────────────────
    sectionHeading: {
        backgroundColor: lightBlue,
        border: "1pt solid #90caf9",
        padding: "5 8",
        marginBottom: 0,
        marginTop: 6,
        fontFamily: "Helvetica-Bold",
        fontSize: 10,
        color: blue,
    },

    // ── ACADEMIC TABLE ───────────────────────────────────────
    table: {
        border: "1pt solid #ccc",
    },
    // Header row 1
    tableHeaderRow1: {
        flexDirection: "row",
        backgroundColor: "#ecf5ff",
    },
    // Header row 2
    tableHeaderRow2: {
        flexDirection: "row",
        backgroundColor: "#f5f5f5",
    },
    tableRow: {
        flexDirection: "row",
    },
    tableRowAlt: {
        flexDirection: "row",
        backgroundColor: "#fafafa",
    },
    // Cells
    cellBase: {
        borderRight: "1pt solid #ccc",
        borderTop: "1pt solid #ccc",
        padding: "4 3",
        textAlign: "center",
        fontSize: 9,
    },
    subjectCol: { width: "22%", textAlign: "left", paddingLeft: 5 },
    assessmentGroupCol: { width: "39%", textAlign: "center" },
    grandTotalGroupCol: { width: "39%", textAlign: "center", borderRight: "none" },
    markCol10: { width: "13%", textAlign: "center" },
    markCol90: { width: "13%", textAlign: "center" },
    totalCol: { width: "6.5%", textAlign: "center" },
    obtCol: { width: "6.5%", textAlign: "center", borderRight: "none" },
    boldCell: { fontFamily: "Helvetica-Bold" },

    // ── OVERALL ROW ──────────────────────────────────────────
    overallRow: {
        flexDirection: "row",
        border: "1pt solid #ccc",
        marginTop: 7,
        borderRadius: 2,
        overflow: "hidden",
    },
    overallCell: {
        flex: 1,
        padding: "5 4",
        borderRight: "1pt solid #ccc",
        textAlign: "center",
        fontSize: 9,
    },
    overallLabel: {
        fontSize: 9,
        color: "#555",
        marginBottom: 2,
        fontFamily: "Helvetica-Bold",
    },
    overallValue: {
        fontSize: 9,
        color: blue,
        fontFamily: "Helvetica-Bold",
    },
    overallFailed: {
        fontSize: 9,
        color: "red",
        fontFamily: "Helvetica-Bold",
    },

    // ── ATTENDANCE ───────────────────────────────────────────
    attendanceRow: {
        border: "1pt solid #ccc",
        flexDirection: "row",
        padding: "5 10",
        marginTop: 0,
        justifyContent: "space-between",
    },
    attendanceLabel: { color: "#333", fontSize: 9 },
    attendanceValue: { color: blue, fontFamily: "Helvetica-Bold", fontSize: 9 },

    // ── FOOTER ───────────────────────────────────────────────
    footer: {
        marginTop: 25,
        flexDirection: "row",
        justifyContent: "space-between",
        fontSize: 9,
    },
    signatureLine: {
        borderTop: "1pt solid #333",
        width: 100,
        textAlign: "center",
        paddingTop: 2,
        marginTop: 30
    },

    // ── BAR CHART ────────────────────────────────────────────
    chartSection: {
        flexDirection: "row",
        marginTop: 13,
        gap: 12,
    },
    barChartBox: {
        flex: 1,
        border: "1pt solid #ccc",
        borderRadius: 3,
        padding: "8 10",
    },
    barChartTitle: {
        fontSize: 10,
        fontFamily: "Helvetica-Bold",
        color: blue,
        marginBottom: 7,
    },
    barRow: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 7,
    },
    barLabel: { width: 44, fontSize: 9, color: "#333" },
    barTrack: {
        flex: 1,
        height: 10,
        backgroundColor: "#e0e0e0",
        borderRadius: 2,
    },
    barFill: {
        height: 10,
        backgroundColor: "#1976d2",
        borderRadius: 2,
    },
    barVal: { width: 28, textAlign: "right", fontSize: 9, color: "#333" },

    // ── GRADING SCALE ─────────────────────────────────────────
    gradeBox: {
        border: "1pt solid #ccc",
        borderRadius: 3,
        padding: "8 10",
        width: 150,
    },
    gradeTitle: {
        fontSize: 9,
        fontFamily: "Helvetica-Bold",
        color: blue,
        marginBottom: 5,
        textAlign: "center",
    },
    gradeRow: {
        flexDirection: "row",
        marginBottom: 3,
    },
    gradeCol: {
        width: "50%",
        flexDirection: "row",
        gap: 4,
    },
    gradeRange: { width: 35, fontSize: 9, color: "#444" },
    gradeVal: { fontSize: 9, fontFamily: "Helvetica-Bold", color: blue, width: 16 },
});




const gradeScale = [
    ["91-100", "A1", "51-60", "C1"],
    ["81-90", "A2", "41-50", "C2"],
    ["71-80", "B1", "33-40", "D"],
    ["61-70", "B2", "0-32", "E"],
];



function StudentPerformanceNewPDF({
    student,
    attendanceDetails,
    assessmentTypeId,
    skillsData,
    studenRankData,
    TotalScore,
    subjectWiseData,
    matchedSubjects,
    selected,
    schoolProfileLogo,
    studentProfileImage
}) {

    const getSubjectTotal = (s) => {
        return selected.reduce((sum, a) => {
            const obtained = s.assessments?.[a.id] || 0;
            const weight = a.weight || 0;

            return sum + (obtained / 100) * weight;
        }, 0);
    };

    const totalObt = subjectWiseData.reduce(
        (sum, s) => sum + getSubjectTotal(s),
        0
    );
    const totalMax =
        subjectWiseData.length *
        selected.reduce((sum, a) => sum + a.weight, 0);
    const percentage = totalMax
        ? ((totalObt / totalMax) * 100).toFixed(2)
        : 0;
    const assessmentTotals = selected.reduce((acc, a) => {
        acc[a.id] = 0;
        return acc;
    }, {});

    subjectWiseData.forEach((s) => {
        selected.forEach((a) => {
            const marks = s.assessments?.[a.id] || 0;
            assessmentTotals[a.id] += marks;
        });
    });
    const isFail = subjectWiseData.some(s => getSubjectTotal(s) < 33);
    const result = isFail ? "Failed" : "Passed";

    const getGrade = (percentage) => {
        if (percentage >= 91) return "A1";
        if (percentage >= 81) return "A2";
        if (percentage >= 71) return "B1";
        if (percentage >= 61) return "B2";
        if (percentage >= 51) return "C1";
        if (percentage >= 41) return "C2";
        if (percentage >= 33) return "D";
        return "E";
    };

    const grade = getGrade(Number(percentage));

    const getSubjectObtained = (s) => {
        return selected.reduce((sum, a) => {
            return sum + (s.assessments?.[a.id] || 0);
        }, 0);
    };
    const today = new Date();

    const formattedDate = today.toLocaleDateString("en-GB");
    const formatDateofbirth = (dateStr) => {
        return new Date(dateStr).toLocaleDateString("en-GB").replace(/\//g, "-");
    };

    function LogoCircle({ text, student }) {
        return (
            <View style={styles.logoBox}>
                <Image src={
                    schoolProfileLogo
                        ? schoolProfileLogo
                        : "images/default_school_logo.jpg"
                }
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                    }} />
            </View>
        );
    }

    function StudentProfile({ student }) {
        return (
            <View style={styles.infoPhotoBox}>
                <Image src={
                    studentProfileImage
                    && getStudentImage(student)
                }
                    style={{
                        width: "100%",
                        height: "100%",
                    }} />
            </View>
        );
    }

    const getStudentImage = (student) => {
        if (student?.profile_picture) {
            const cleanPath = student.profile_picture;
            return `${IP}${cleanPath}`;  // ✅ Include IP here
        }
        if (student?.gender === "M") {
            return student?.avatar_profile
                ? `/images/Boys/${student.avatar_profile}.webp`
                : `/images/Student_boy_pic.png`;
        }
        if (student?.gender === "F") {
            return student?.avatar_profile
                ? `/images/Girls/${student.avatar_profile}.webp`
                : `/images/Student_girl_pic.png`;
        }
        return `/images/default image.png`;
    };

    return (
        <Document>
            <Page size="A4" style={styles.page}>

                {/* ── HEADER ── */}
                <View style={styles.headerBox}>
                    <LogoCircle text="SCHOOL" student={student} />
                    <View style={styles.headerCenter}>
                        <Text style={styles.schoolName}>{student?.school?.name}</Text>
                        <Text style={styles.reportTitle}>Report Card</Text>
                        <Text style={styles.sessionText}>Session : {student?.academicYear?.name}</Text>
                    </View>
                    <LogoCircle text="SCHOOL" student={student} />
                </View>

                {/* ── STUDENT INFO ── */}
                <View style={styles.infoBox}>
                    <View style={styles.infoLeft}>
                        {[
                            ["Name of the Student", student?.name],
                            ["Admission Number", student?.admissionNumber],
                            ["Date of Birth", formatDateofbirth(student.dateOfBirth)],
                            ["Father's Name", student?.father?.name],
                            ["Father's Mobile No", student?.father?.mobileNumber],
                            ["Address", "NA"],
                        ].map(([label, val], i) => (
                            <View key={i} style={styles.infoLine}>
                                <Text style={styles.infoLabel}>{label}</Text>
                                <Text style={styles.infoColon}>:</Text>
                                <Text style={styles.infoValue}>{val}</Text>
                            </View>
                        ))}
                    </View>
                    <View style={styles.infoRight}>
                        {[
                            ["Class", student?.standard?.name],
                            ["Roll No.", student?.code],
                            ["House", "NA"],
                            ["Mother's Name", student?.mother?.name],
                            ["Mother's Mobile", student?.mother?.mobileNumber],
                        ].map(([label, val], i) => (
                            <View key={i} style={styles.infoLine}>
                                <Text style={styles.infoLabel}>{label}</Text>
                                <Text style={styles.infoColon}>:</Text>
                                <Text style={styles.infoValue}>{val}</Text>
                            </View>
                        ))}
                    </View>
                    {/* Photo placeholder */}
                    <StudentProfile student={student} />
                </View>

                {/* ── ACADEMIC ACHIEVEMENTS ── */}
                <View style={styles.sectionHeading}>
                    <Text>ACADEMIC ACHIEVEMENTS</Text>
                </View>

                <View style={styles.table}>
                    {/* HEADER ROW 1 */}
                    <View style={styles.tableHeaderRow1}>
                        <Text style={[styles.cellBase, styles.subjectCol, styles.boldCell]}>
                            SUBJECT
                        </Text>
                        {/* 
                        <Text style={[styles.cellBase, { width: "30%", fontFamily: "Helvetica-Bold" }]}>
                            Assessment
                        </Text>

                        <Text style={[styles.cellBase, { width: "30%", fontFamily: "Helvetica-Bold", }]}>
                            Assessment
                        </Text> */}
                        {selected.map((a, i) => (
                            <Text key={i} style={[styles.cellBase, { width: `${60 / selected.length}%` }]}>
                                {a.name}
                            </Text>
                        ))}


                        <Text style={[styles.cellBase, { width: "25%", fontFamily: "Helvetica-Bold", }]}>
                            Grand Total
                        </Text>
                    </View>

                    {/* HEADER ROW 2 */}
                    <View style={styles.tableHeaderRow2}>
                        <Text style={[styles.cellBase, styles.subjectCol]}>{""}</Text>

                        {selected.map((a, i) => (
                            <Text key={i} style={[styles.cellBase, { width: `${60 / selected.length}%`, fontFamily: "Helvetica-Bold" }]}>
                                Marks Obtained{"\n"}TH {a.weight}
                            </Text>
                        ))}

                        <View style={[styles.cellBase, { width: "25%" }]}>
                            <Text style={{ textAlign: "center", fontFamily: "Helvetica-Bold" }}>
                                Marks Obtained
                            </Text>

                            <View style={{ flexDirection: "row" }}>
                                <View style={{ flex: 1 }}>
                                    <Text style={{ textAlign: "center" }}>Total</Text>
                                    <Text style={{ textAlign: "center" }}>Marks</Text>
                                </View>

                                <View style={{ flex: 1 }}>
                                    <Text style={{ textAlign: "center" }}>Obt.</Text>
                                    <Text style={{ textAlign: "center" }}>Marks</Text>
                                </View>
                            </View>
                        </View>
                    </View>

                    {/* Subject rows */}
                    {subjectWiseData.map((s, i) => {
                        console.log("s>>>>>", s);

                        // const subjectName = s.result[0]?.name || "N/A";
                        const subjectTotal = selected.reduce((sum, a) => {
                            const obtained = s.assessments?.[a.id] || 0;
                            const weight = a.weight || 0;
                            return sum + (obtained / 100) * weight;
                        }, 0);

                        console.log("subjectTotal", subjectTotal);


                        return (
                            <View key={i} style={i % 2 === 0 ? styles.tableRow : styles.tableRowAlt}>
                                <Text style={[styles.cellBase, styles.subjectCol]}>
                                    {s?.subject}
                                </Text>


                                {selected.map((a, idx) => (
                                    <Text key={idx} style={[styles.cellBase, { width: `${60 / selected.length}%` }]}>
                                        {s.assessments?.[a.id] || 0}
                                    </Text>
                                ))}


                                {/* TOTAL */}
                                <View style={[
                                    styles.cellBase,
                                    { width: "25%", flexDirection: "row", borderRight: "none", padding: 0 }
                                ]}>
                                    <View style={{ flex: 1, alignItems: "center" }}>
                                        <Text style={{ fontFamily: "Helvetica-Bold" }}>
                                            {100}
                                        </Text>
                                    </View>

                                    <View style={{ flex: 1, alignItems: "center" }}>
                                        <Text style={{ fontFamily: "Helvetica-Bold" }}>
                                            {subjectTotal.toFixed(1)}
                                        </Text>
                                    </View>
                                </View>
                            </View>
                        );
                    })}

                    {/* TOTAL row */}
                    <View style={[styles.tableRow, { backgroundColor: "#ecf5ff" }]}>

                        <Text style={[styles.cellBase, styles.subjectCol]}>
                            Total
                        </Text>

                        {/* ✅ Assessment wise totals */}
                        {selected.map((a, i) => (
                            <Text
                                key={i}
                                style={[
                                    styles.cellBase,
                                    { width: `${60 / selected.length}%`, fontFamily: "Helvetica-Bold" }
                                ]}
                            >
                                {assessmentTotals[a.id] || 0}
                            </Text>
                        ))}

                        {/* ✅ Grand total column */}

                        <View
                            style={[
                                styles.cellBase,
                                {
                                    width: "25%",
                                    flexDirection: "row",
                                    borderRight: "none",
                                    padding: 0
                                }
                            ]}
                        >
                            {/* Total Marks */}
                            <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
                                <Text style={{ fontFamily: "Helvetica-Bold" }}>
                                    {totalMax}
                                </Text>
                            </View>

                            {/* Obtained Marks */}
                            <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
                                <Text style={{ fontFamily: "Helvetica-Bold" }}>
                                    {totalObt.toFixed(1)}
                                </Text>
                            </View>
                        </View>
                    </View>
                </View>

                {/* ── OVERALL SUMMARY ── */}
                <View style={styles.overallRow}>
                    <View style={[styles.overallCell, { backgroundColor: "#ecf5ff" }]}>
                        <Text style={styles.overallLabel}>Overall Marks</Text>
                        <Text style={styles.overallValue}>{totalObt} /{totalMax}</Text>
                    </View>
                    <View style={styles.overallCell}>
                        <Text style={styles.overallLabel}>Percentage</Text>
                        <Text style={styles.overallValue}>{percentage}%</Text>
                    </View>
                    <View style={[styles.overallCell, { backgroundColor: "#fff5f5" }]}>
                        <Text style={styles.overallLabel}>Result</Text>
                        <Text style={result === "Failed" ? styles.overallFailed : styles.overallValue}>
                            {result}
                        </Text>
                    </View>
                    <View style={[styles.overallCell, { borderRight: "none" }]}>
                        <Text style={styles.overallLabel}>Overall Grade</Text>
                        <Text style={styles.overallValue}>{grade}</Text>
                    </View>
                </View>

                {/* ── ATTENDANCE ── */}
                <View style={[styles.sectionHeading, { marginTop: 10 }]}>
                    <Text>ATTENDANCE</Text>
                </View>
                <View style={styles.attendanceRow}>
                    <View style={{ flexDirection: "row" }}>
                        <Text style={styles.attendanceLabel}>Total Working Days : </Text>
                        <Text style={styles.attendanceValue}>{attendanceDetails?.workingDays}</Text>
                    </View>
                    <View style={{ flexDirection: "row" }}>
                        <Text style={styles.attendanceLabel}>Total Presents : </Text>
                        <Text style={styles.attendanceValue}>{attendanceDetails?.presentCount}</Text>
                    </View>
                </View>

                {/* ── FOOTER ── */}
                <View style={styles.footer}>
                    <Text>Date : {formattedDate}</Text>
                    <View style={{ alignItems: "center" }}>
                        <View style={styles.signatureLine}>
                            <Text>Principal</Text>
                        </View>
                    </View>
                </View>

                {/* ── BAR CHART + GRADING SCALE ── */}
                <View style={styles.chartSection}>

                    {/* Bar Chart */}
                    <View style={styles.barChartBox}>
                        <Text style={styles.barChartTitle}>Subject Performance</Text>

                        {subjectWiseData.map((s, i) => {
                            const obtained = getSubjectObtained(s);

                            // 👉 calculate max marks dynamically
                            const maxMarks = selected.reduce((sum, a) => sum + (a.maxMarks || 100), 0);

                            const percent = (obtained / maxMarks) * 100;

                            return (
                                <View key={i} style={styles.barRow}>
                                    <Text style={styles.barLabel}>
                                        {s.subject}
                                    </Text>

                                    <View style={styles.barTrack}>
                                        <View
                                            style={[
                                                styles.barFill,
                                                { width: `${percent}%` }
                                            ]}
                                        />
                                    </View>

                                    <Text style={styles.barVal}>
                                        {obtained}
                                    </Text>
                                </View>
                            );
                        })}
                    </View>

                    {/* Grading Scale */}
                    <View style={styles.gradeBox}>
                        <Text style={styles.gradeTitle}>Grading Scale</Text>
                        {gradeScale.map((row, i) => (
                            <View key={i} style={styles.gradeRow}>
                                <View style={styles.gradeCol}>
                                    <Text style={styles.gradeRange}>{row[0]}</Text>
                                    <Text style={styles.gradeVal}>{row[1]}</Text>
                                </View>
                                <View style={styles.gradeCol}>
                                    <Text style={styles.gradeRange}>{row[2]}</Text>
                                    <Text style={styles.gradeVal}>{row[3]}</Text>
                                </View>
                            </View>
                        ))}
                    </View>

                </View>

            </Page>
        </Document>
    );

}

export default StudentPerformanceNewPDF;
